import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { AllowedLanguages } from 'i18n';

import { useCreatePartner, useCurrentPartner } from 'services/repositories/partners/partners';
import { useCreateTradeRequest } from 'services/repositories/relationships/relationships';
import { useNotificationStore } from 'stores/notifications/notifications';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { classNames } from 'support/helpers/generic/generic';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { isSupplierAccordingToDocumentTypes } from 'support/helpers/relationships/relationships';
import { ConnectionsRoutes, NetworkRoutes } from 'support/types';
import { SlideOver } from 'components/Display/SlideOver/SlideOver';
import { DocumentTypeRequestModal } from 'components/DocumentTypeRequestModal/DocumentTypeRequestModal';
import { Button } from 'components/Form/Button/Button';
import { CheckboxInABox } from 'components/Form/Checkbox/Checkbox';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';
import { Form } from 'components/Form/Form';
import { Input } from 'components/Form/Input/Input/Input';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';
import { UnsupportedDocumentTypesMessage } from 'components/UnsupportedDocumentTypesMessage/UnsupportedDocumentTypesMessage';

import { DocumentSettingsCheckboxes } from '../DocumentSettingsCheckboxes/DocumentSettingsCheckboxes';
import {
  createDocumentTypesPayload,
  createTradePartnerRequests,
  defaultDocumentSettings,
  DocumentTypeSetting,
  mapPartnerDocumentTypes,
} from '../helpers';
import { PartnerIdentifiers } from '../PartnerIdentifiers/PartnerIdentifiers';
import { TradePartnerSuggestion } from '../TradePartnerSuggestion/TradePartnerSuggestion';
import { TradeRequestMessage } from '../TradeRequestMessage/TradeRequestMessage';
import { defaultData, isFormWithPartnerId, NewTradePartnerForm, NewTradePartnerFormSchema } from './formHelpers';

type NewTradePartnerProps = { showPopup: boolean; setShowPopup: (value: boolean) => void };

export function NewTradePartner({ showPopup, setShowPopup }: NewTradePartnerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotificationStore();
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);
  const [showDocumentTypeRequestModal, setShowDocumentTypeRequestModal] = useState(false);
  const formMethods = useForm<NewTradePartnerForm>({
    defaultValues: defaultData,
    resolver: yupResolver(NewTradePartnerFormSchema(t)),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    formState: { errors },
  } = formMethods;

  const [tradePartnerName, tradePartnerId] = useWatch({ name: ['name', 'partnerId'], control });
  const { data: currentPartner } = useCurrentPartner();
  const [documentTypeSettings, setDocumentTypeSettings] = useState<Array<DocumentTypeSetting>>(defaultDocumentSettings);

  useEffect(() => {
    if (currentPartner) {
      setDocumentTypeSettings(
        mapPartnerDocumentTypes({ partnerDocuments: currentPartner.documentTypes, isCurrentPartner: true }),
      );
    }
  }, [currentPartner]);

  const createPartnerMutation = useCreatePartner();
  const createTradeRequestMutation = useCreateTradeRequest();
  const isLoading = createPartnerMutation.isLoading || createTradeRequestMutation.isLoading;

  const onSubmit = async (submission: NewTradePartnerForm) => {
    if (!currentPartner) {
      return;
    }

    setNonInputErrors([]);
    const requestedDocumentTypes = documentTypeSettings.filter(
      (setting) => setting.outgoing.selected || setting.incoming.selected,
    );
    if (!requestedDocumentTypes.length) {
      setNonInputErrors([t('partners:request.noDocumentTypesSelected')]);
      return;
    }

    if (isFormWithPartnerId(submission)) {
      const relationships = createDocumentTypesPayload(currentPartner.id, submission.partnerId, requestedDocumentTypes);
      createTradeRequestMutation.mutate(
        {
          tradeRequests: [
            {
              partnerId: submission.partnerId,
              relationships,
              message: submission.message,
              partyIdentifierDomain: submission.partyIdentifierDomain,
              partyIdentifier: submission.partyIdentifier,
              includeAsCCRecipient: submission.includeAsCCRecipient,
            },
          ],
        },
        {
          onSuccess: () => {
            onClose();
            addNotification(t('partners:request.newTradePartner.success'));

            const isNetworkContext = location.pathname.startsWith(NetworkRoutes.root);
            const route = isNetworkContext ? NetworkRoutes.connectionDetails : ConnectionsRoutes.connectionDetails;
            navigate(routeToPage(route, { partnerId: submission.partnerId }));
          },
          onError: (err) => {
            processSubmissionErrors({
              error: err as Error,
              setInputError: setError,
              setNonInputErrors,
              defaultData,
              t,
            });
          },
        },
      );
      return;
    }

    try {
      const partnerIds = await createTradePartnerRequests({
        createPartnerMutation: createPartnerMutation,
        createTradeRequestMutation: createTradeRequestMutation,
        currentPartner,
        submission: {
          requestedDocumentTypes,
          message: submission.message,
          includeAsCCRecipient: submission.includeAsCCRecipient,
          tradePartners: [
            {
              name: submission.name,
              contactPersons: submission.contactPersons,
              partyIdentifierDomain: submission.partyIdentifierDomain,
              partyIdentifier: submission.partyIdentifier,
            },
          ],
        },
      });
      onClose();
      addNotification(t('partners:request.newTradePartner.success'));

      const newPartnerId = partnerIds[0];
      navigate(routeToPage(NetworkRoutes.connectionDetails, { partnerId: newPartnerId }));
    } catch (err) {
      processSubmissionErrors({ error: err as Error, setInputError: setError, setNonInputErrors, defaultData, t });
    }
  };

  const onClose = () => {
    setShowPopup(false);
    setNonInputErrors([]);
    reset();
    setDocumentTypeSettings(
      currentPartner
        ? mapPartnerDocumentTypes({ partnerDocuments: currentPartner.documentTypes, isCurrentPartner: true })
        : defaultDocumentSettings,
    );
  };

  const PrimaryButton = () => (
    <Button
      loading={isLoading}
      type="submit"
      form="newTradePartnerForm"
      analyticsId="partners:new_trade_partner_request"
    >
      <div data-testid="action-button">{t('partners:request.newTradePartner.primaryButton')}</div>
    </Button>
  );

  const CancelButton = () => (
    <Button disabled={isLoading} onClick={onClose} variant="secondary" analyticsId="partners:new_trade_partner_cancel">
      <div data-testid="cancel-button">{t('common:cancel')}</div>
    </Button>
  );

  const hasSelectedDocumentTypes = documentTypeSettings.some(
    (setting) => setting.outgoing.selected || setting.incoming.selected,
  );

  const loggedInPartnerIsASupplier = useMemo(() => {
    const sentDocumentTypes = documentTypeSettings
      .filter((setting) => !!setting.outgoing.selected)
      .map(({ documentType }) => documentType);
    const receivedDocumentTypes = documentTypeSettings
      .filter((setting) => !!setting.incoming.selected)
      .map(({ documentType }) => documentType);

    return isSupplierAccordingToDocumentTypes(sentDocumentTypes, receivedDocumentTypes);
  }, [documentTypeSettings]);

  return (
    <SlideOver
      size="large"
      open={showPopup}
      setOpen={onClose}
      title={t('partners:request.newTradePartner.heading')}
      subtitle={t('partners:request.newTradePartner.subHeading')}
      footer={
        <>
          <CancelButton />
          <PrimaryButton />
        </>
      }
    >
      <div className={classNames(nonInputErrors.length ? 'mb-6' : undefined)}>
        <ValidationErrors errors={nonInputErrors} />
      </div>

      <FormProvider {...formMethods}>
        <Form
          id="newTradePartnerForm"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6 divide-y divide-gray-200 [&>*:first-child]:pt-0 [&>*]:pt-6"
        >
          <div className="mb-5 flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-2">
              <Input
                label={t('partners:request.tradePartnerName')}
                {...register('name')}
                required
                errors={errors.name}
              />

              <Input
                label={t('partners:request.contactPerson')}
                {...register('contactPersons.0.name')}
                required
                disabled={Boolean(tradePartnerId)}
                errors={errors.contactPersons?.[0]?.name}
              />

              <Input
                label={t('partners:request.email')}
                type="email"
                {...register('contactPersons.0.email')}
                required
                errors={errors.contactPersons?.[0]?.email}
                disabled={Boolean(tradePartnerId)}
              />

              <Input
                label={t('partners:request.phone')}
                {...register('contactPersons.0.phone')}
                required
                errors={errors.contactPersons?.[0]?.phone}
                disabled={Boolean(tradePartnerId)}
              />

              <Controller
                render={({ field, fieldState }) => (
                  <Dropdown<AllowedLanguages>
                    {...field}
                    required
                    wrapperClassName="col-span-full"
                    label={t('partners:request.language')}
                    options={[
                      { value: 'de', label: t('common:language.de') },
                      { value: 'en', label: t('common:language.en') },
                    ]}
                    errors={fieldState.error}
                  />
                )}
                name="contactPersons.0.language"
              />
            </div>
            <TradePartnerSuggestion tradePartnerName={tradePartnerName} />
          </div>

          <div>
            <DocumentSettingsCheckboxes
              documentTypeSettings={documentTypeSettings}
              setDocumentTypeSettings={setDocumentTypeSettings}
            />
            <div className="mt-4">
              <UnsupportedDocumentTypesMessage
                documentTypeSettings={documentTypeSettings}
                onRequestUnavailableDocuments={() => setShowDocumentTypeRequestModal(true)}
              />
            </div>
          </div>

          <PartnerIdentifiers />

          <div className="space-y-6">
            {hasSelectedDocumentTypes && !loggedInPartnerIsASupplier && <TradeRequestMessage />}

            <CheckboxInABox
              label={t('partners:request.includeAsCCRecipient.label')}
              description={t('partners:request.includeAsCCRecipient.description')}
              {...register('includeAsCCRecipient')}
            />
          </div>
        </Form>
      </FormProvider>
      <DocumentTypeRequestModal
        open={showDocumentTypeRequestModal}
        onClose={() => setShowDocumentTypeRequestModal(false)}
      />
    </SlideOver>
  );
}
